/**
 * WireguardPanel (rc148) — pestaña "WireGuard" de la página VPN.
 *
 *   - Tarjeta del servidor wg-byteisp (endpoint, subred, llave, si responde)
 *   - Tabla de peers con estado en vivo y último contacto
 *   - Crear / editar (vincular router), script RouterOS, regenerar, eliminar
 *   - Auto-refresco cada 30 s
 *
 * Convive con OpenVPN: esta pestaña no toca nada de las credenciales OVPN.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowDown, ArrowUp, Copy, KeyRound, Pencil, Plus, RefreshCw, Search, Server, Terminal, Trash2,
  TriangleAlert, Upload, Waypoints,
} from "lucide-react";

import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { useDebounce } from "@/hooks/useDebounce";
import { formatDateTime } from "@/lib/utils";
import type { ApiError } from "@/lib/api";

import { useAplicarWg, useBorrarWgPeer, useRegenerarWgPeer, useWgEstado, useWgPeers } from "./wgApi";
import { formatBytes, haceCuanto, type WgEstado, type WgPeer } from "./wgSchemas";
import { WgPeerFormDialog, avisarAplicacion } from "./WgPeerFormDialog";
import { WgScriptDialog } from "./WgScriptDialog";

export function WireguardPanel() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 300);
  const filters = useMemo(() => ({ page, size: 30, search }), [page, search]);

  const { data: estado } = useWgEstado();
  const { data, isLoading, isFetching } = useWgPeers(filters);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<WgPeer | null>(null);
  const [scriptId, setScriptId] = useState<string | null>(null);

  const regenerarMut = useRegenerarWgPeer();
  const borrarMut = useBorrarWgPeer();

  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  async function handleRegenerar(p: WgPeer) {
    if (!confirm(
      `¿Regenerar las llaves de "${p.descripcion}"?\n\n` +
      "El router deja de conectar hasta que pegues el script nuevo." +
      (p.router_usa_wg ? "\n\nOJO: ByteISP usa este túnel para hablar con el router." : ""),
    )) return;
    try {
      const res = await regenerarMut.mutateAsync(p.id);
      avisarAplicacion(res.aplicacion, "Llaves regeneradas");
      setScriptId(p.id);
    } catch (e) {
      toast.error("No se pudo regenerar", { description: (e as ApiError).message });
    }
  }

  async function handleBorrar(p: WgPeer) {
    if (!confirm(`¿Eliminar el peer "${p.descripcion}" (${p.ip_tunel})?\n\nSe quita del servidor y su IP queda libre.`)) return;
    try {
      const res = await borrarMut.mutateAsync(p.id);
      avisarAplicacion(res.aplicacion, "Peer eliminado");
    } catch (e) {
      toast.error("No se pudo eliminar", { description: (e as ApiError).message });
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Waypoints size={16} /> WireGuard
          </h2>
          <p className="text-xs text-muted-foreground">
            Túnel de gestión hacia los MikroTik (RouterOS 7). Convive con OpenVPN:
            migra router por router desde su ficha.
          </p>
        </div>
        <Button size="sm" className="shrink-0 whitespace-nowrap" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> Nuevo peer
        </Button>
      </div>

      <ServidorCard estado={estado} />

      <div className="flex flex-wrap items-end gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
            placeholder="Buscar por descripción, router o IP…"
            className="pl-8"
          />
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Estado</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead>Router</TableHead>
              <TableHead>IP túnel</TableHead>
              <TableHead>Último contacto</TableHead>
              <TableHead>Tráfico</TableHead>
              <TableHead>Origen</TableHead>
              <TableHead className="pr-4 text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Waypoints}
                  title="Sin peers WireGuard"
                  description={search ? "Sin coincidencias." : "Crea el primer peer y pega su script en el MikroTik."}
                  action={!search ? (
                    <Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={14} /> Nuevo peer</Button>
                  ) : undefined}
                />
              </TableEmpty>
            ) : (
              items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell><EstadoBadge peer={p} /></TableCell>
                  <TableCell className="font-medium">{p.descripcion}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {p.router_nombre ? (
                      <span className="inline-flex items-center gap-1.5">
                        {p.router_nombre}
                        {p.router_usa_wg && <Badge variant="default" title="ByteISP habla con este router por WireGuard">en uso</Badge>}
                      </span>
                    ) : <span className="italic">— Sin asignar</span>}
                  </TableCell>
                  <TableCell className="font-mono">{p.ip_tunel}</TableCell>
                  <TableCell className="text-muted-foreground" title={p.last_handshake_at ? formatDateTime(p.last_handshake_at) : undefined}>
                    {haceCuanto(p.last_handshake_at)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                    {p.rx_bytes || p.tx_bytes ? (
                      <span className="inline-flex items-center gap-1">
                        <ArrowDown size={10} />{formatBytes(p.rx_bytes)}
                        <ArrowUp size={10} className="ml-1" />{formatBytes(p.tx_bytes)}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">{p.endpoint ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <Button variant="ghost" size="icon" onClick={() => setScriptId(p.id)} title="Script RouterOS">
                        <Terminal size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setEditing(p)} title="Editar / vincular router">
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleRegenerar(p)} disabled={regenerarMut.isPending} title="Regenerar llaves">
                        <KeyRound size={14} />
                      </Button>
                      <Button
                        variant="ghost" size="icon" onClick={() => handleBorrar(p)}
                        disabled={borrarMut.isPending || p.router_usa_wg}
                        title={p.router_usa_wg ? "El router usa este túnel: regrésalo a OpenVPN primero" : "Eliminar"}
                      >
                        <Trash2 size={14} className="text-danger" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {(totalPages > 1 || isFetching) && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}{isFetching && " · actualizando…"}
            </span>
            {totalPages > 1 && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((x) => Math.max(1, x - 1))}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((x) => x + 1)}>Siguiente</Button>
              </div>
            )}
          </div>
        )}
      </div>

      <WgPeerFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        peer={null}
        onCreated={(p) => { setCreateOpen(false); setScriptId(p.id); }}
      />
      <WgPeerFormDialog
        open={editing !== null}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        peer={editing}
      />
      <WgScriptDialog
        peerId={scriptId}
        open={scriptId !== null}
        onOpenChange={(v) => { if (!v) setScriptId(null); }}
      />
    </div>
  );
}

// ─── Sub-componentes ─────────────────────────────────────────────────────

export function EstadoBadge({ peer }: { peer: WgPeer }) {
  switch (peer.estado) {
    case "conectado": return <Badge variant="success">En vivo</Badge>;
    case "sin_contacto": return <Badge variant="warning">Sin contacto</Badge>;
    case "inactivo": return <Badge variant="secondary">Inactivo</Badge>;
    default: return <Badge variant="secondary" className="opacity-70">Nunca</Badge>;
  }
}

function Dato({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`truncate text-xs ${mono ? "font-mono" : ""}`}>{children}</div>
    </div>
  );
}

function ServidorCard({ estado }: { estado: WgEstado | undefined }) {
  const aplicarMut = useAplicarWg();
  const llave = estado?.server_public_key ?? estado?.public_key_vivo ?? null;

  async function aplicar() {
    try {
      const res = await aplicarMut.mutateAsync();
      avisarAplicacion(res, "Peers enviados al servidor");
    } catch (e) {
      toast.error("No se pudo aplicar", { description: (e as ApiError).message });
    }
  }

  async function copiarLlave() {
    if (!llave) return;
    try { await navigator.clipboard.writeText(llave); toast.success("Llave pública copiada"); }
    catch { toast.error("No se pudo copiar"); }
  }

  if (!estado) {
    return <div className="rounded-lg border bg-card p-3 text-xs text-muted-foreground">Cargando servidor…</div>;
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Server size={14} className="text-electric" />
          Servidor <span className="font-mono">{estado.interfaz}</span>
          {estado.interfaz_responde
            ? <Badge variant="success">Responde</Badge>
            : <Badge variant="destructive">No responde</Badge>}
          {estado.simulado && <Badge variant="warning">Simulado</Badge>}
        </div>
        <div className="flex items-center gap-3 font-mono text-[11px] text-muted-foreground">
          <span><span className="text-success">{estado.conectados}</span> en vivo</span>
          <span>{estado.activos}/{estado.total} activos</span>
          <span>{estado.routers_en_wg} router(s) en WG</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-4">
        <Dato label="Endpoint" mono>{estado.endpoint_host}:{estado.listen_port}/udp</Dato>
        <Dato label="Servidor en el túnel" mono>{estado.server_ip}</Dato>
        <Dato label="Subred" mono>{estado.subnet}</Dato>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Llave pública</div>
          <div className="flex items-center gap-1">
            <span className="truncate font-mono text-xs">{llave ?? "—"}</span>
            {llave && (
              <button type="button" onClick={copiarLlave} className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground" title="Copiar llave pública">
                <Copy size={11} />
              </button>
            )}
          </div>
        </div>
      </div>

      {!estado.enabled && !estado.simulado && (
        <Aviso tono="warn">
          WireGuard aún no está instalado en este servidor. Corre <code className="font-mono">install.sh --vpn ambos</code>:
          puedes crear peers desde ya y se aplicarán al instalarlo.
        </Aviso>
      )}
      {estado.enabled && !estado.interfaz_responde && estado.error_estado && (
        <Aviso tono="danger">{estado.error_estado}</Aviso>
      )}
      {estado.llave_coincide === false && (
        <Aviso tono="danger">
          La llave pública guardada no coincide con la de la interfaz. Vuelve a correr install.sh
          antes de repartir scripts.
        </Aviso>
      )}
      {estado.pendiente_aplicar && (estado.enabled || estado.simulado) && (
        <Aviso tono="warn" accion={
          <Button size="sm" variant="outline" onClick={aplicar} disabled={aplicarMut.isPending}>
            {aplicarMut.isPending ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
            Aplicar ahora
          </Button>
        }>
          Hay cambios pendientes de aplicar en el servidor.
          {estado.ultimo_apply && <span className="text-muted-foreground"> Último intento: {estado.ultimo_apply}</span>}
        </Aviso>
      )}
    </div>
  );
}

function Aviso({ tono, children, accion }: { tono: "warn" | "danger"; children: React.ReactNode; accion?: React.ReactNode }) {
  const cls = tono === "warn"
    ? "border-warn-soft bg-warn-soft text-warn"
    : "border-danger-soft bg-danger-soft text-danger";
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-[11px] ${cls}`}>
      <div className="flex items-start gap-1.5">
        <TriangleAlert size={12} className="mt-0.5 shrink-0" />
        <span>{children}</span>
      </div>
      {accion}
    </div>
  );
}
